import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Navbar, Footer } from "../../components/Layout";
import { useTheme } from "../../contexts/ThemeContext";
import { toast, Toaster } from "sonner";
import { ArrowLeft, Save, Upload, Plus, X, Eye } from "lucide-react";

const SECTIONS = [
  { key: "branding", label: "Identité & Logo" },
  { key: "theme", label: "Thème & couleurs" },
  { key: "menu", label: "Menu & footer" },
  { key: "hero", label: "Bannière d'accueil" },
  { key: "about", label: "Page À propos" },
  { key: "contact", label: "Page Contact" },
];

const POPULAR_FONTS = [
  "Archivo", "Outfit", "Inter", "Manrope", "Space Grotesk", "Bricolage Grotesque",
  "Playfair Display", "DM Serif Display", "Cormorant Garamond",
  "Roboto", "Poppins", "Montserrat", "Work Sans", "Plus Jakarta Sans",
  "JetBrains Mono", "Fira Code", "IBM Plex Mono",
];

export default function SiteContentEditor() {
  const [data, setData] = useState({});
  const [active, setActive] = useState("branding");
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const { reload: reloadTheme } = useTheme();

  useEffect(() => {
    api.get("/site-content").then(r => setData(r.data || {})).catch(() => {});
  }, []);

  const sec = data[active] || {};
  const setField = (k, v) => setData({ ...data, [active]: { ...sec, [k]: v } });

  const uploadFile = async (file, fieldKey) => {
    setUploading(true);
    try {
      const isVideo = file.type.startsWith("video/");
      const endpoint = isVideo ? "/admin/upload-video" : "/admin/upload";
      const fd = new FormData(); fd.append("file", file);
      const { data: res } = await api.post(endpoint, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setField(fieldKey, `${process.env.REACT_APP_BACKEND_URL}${res.url}`);
      toast.success("Média uploadé");
    } catch (e) { toast.error("Échec upload"); }
    finally { setUploading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/admin/site-content/${active}`, { value: data[active] });
      toast.success("Enregistré — modifications appliquées sur le site");
      reloadTheme();
    } catch (e) { toast.error("Erreur de sauvegarde"); }
    finally { setSaving(false); }
  };

  return (
    <div className="min-h-screen" style={{ background: "var(--bg-default)" }}>
      <Navbar />
      <Toaster richColors position="top-right" />
      <section className="max-w-6xl mx-auto px-6 md:px-8 py-12">
        <Link to="/admin" className="text-xs font-bold uppercase tracking-widest hover:opacity-100 mb-6 inline-flex items-center gap-2" style={{ color: "var(--text-secondary)" }}><ArrowLeft className="w-3 h-3"/> Retour</Link>
        <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
          <h1 className="font-display font-black text-3xl md:text-4xl tracking-tight">Contenu & personnalisation du site</h1>
          <a href="/" target="_blank" rel="noreferrer" className="text-xs font-bold uppercase tracking-wider flex items-center gap-2 border border-slate-900 px-3 py-2 hover:bg-slate-900 hover:text-white"><Eye className="w-3 h-3"/>Voir le site</a>
        </div>

        <div className="flex gap-2 border-b mb-8 overflow-x-auto" style={{ borderColor: "var(--border-default)" }}>
          {SECTIONS.map(s => (
            <button key={s.key} onClick={() => setActive(s.key)} data-testid={`section-${s.key}`}
              className={`px-5 py-3 text-sm font-bold uppercase tracking-wider border-b-2 -mb-px whitespace-nowrap ${active === s.key ? "border-[color:var(--solar)]" : "border-transparent"}`}
              style={{ color: active === s.key ? "var(--text-primary)" : "var(--text-secondary)" }}>
              {s.label}
            </button>
          ))}
        </div>

        <div className="bg-[color:var(--bg-surface)] border p-8 space-y-5" style={{ borderColor: "var(--border-default)" }} data-testid="content-editor">
          {active === "branding" && <BrandingEditor sec={sec} setField={setField} uploadFile={uploadFile} uploading={uploading} />}
          {active === "theme" && <ThemeEditor sec={sec} setField={setField} />}
          {active === "menu" && <MenuEditor sec={sec} setField={setField} />}
          {active === "hero" && <HeroEditor sec={sec} setField={setField} uploadFile={uploadFile} uploading={uploading} />}
          {active === "about" && <AboutEditor sec={sec} setField={setField} uploadFile={uploadFile} uploading={uploading} />}
          {active === "contact" && <ContactEditor sec={sec} setField={setField} />}

          <button onClick={save} disabled={saving} data-testid="save-content"
            className="btn-brutal btn-brutal-solar w-full px-6 py-4 font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-3 disabled:opacity-50 mt-6">
            {saving ? "Enregistrement..." : "Enregistrer les modifications"} {!saving && <Save className="w-4 h-4" />}
          </button>
        </div>
      </section>
      <Footer />
    </div>
  );
}

const Field = ({ label, children, hint }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: "var(--text-secondary)" }}>{label}</span>
    {children}
    {hint && <p className="text-xs mt-1" style={{ color: "var(--text-muted)" }}>{hint}</p>}
  </label>
);

const ImageField = ({ label, value, onChange, onUpload, uploading, hint }) => (
  <Field label={label} hint={hint}>
    <div className="flex gap-2 items-stretch">
      <input value={value || ""} onChange={e => onChange(e.target.value)} className="input flex-1" placeholder="https://..." />
      <label className="btn-brutal bg-[color:var(--bg-surface)] border border-slate-900 px-3 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer flex items-center gap-1" data-testid={`upload-${label.toLowerCase().replace(/\s+/g,"-")}`}>
        <Upload className="w-3 h-3"/>{uploading ? "..." : "Upload"}
        <input type="file" accept="image/*,.ico" onChange={e => e.target.files[0] && onUpload(e.target.files[0])} className="hidden" />
      </label>
    </div>
    {value && <img src={value} alt="" className="h-16 max-w-[280px] object-contain mt-3 border border-slate-200 p-2 bg-white"/>}
  </Field>
);

const BrandingEditor = ({ sec, setField, uploadFile, uploading }) => (
  <>
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Nom du site *" hint="Apparaît dans le header et le titre de l'onglet"><input value={sec.site_name || ""} onChange={e => setField("site_name", e.target.value)} className="input" data-testid="branding-site-name" /></Field>
      <Field label="Slogan / Sous-titre"><input value={sec.site_slogan || ""} onChange={e => setField("site_slogan", e.target.value)} className="input" /></Field>
    </div>
    <Field label="Texte du badge header (kicker)" hint="Petit texte au-dessus du titre principal — ex: « Cercottes — 45520 Loiret »">
      <input value={sec.header_tagline || ""} onChange={e => setField("header_tagline", e.target.value)} className="input" />
    </Field>
    <ImageField label="Logo principal (header)" value={sec.logo_main_url} onChange={v => setField("logo_main_url", v)} onUpload={f => uploadFile(f, "logo_main_url")} uploading={uploading} hint="Recommandé : PNG ou SVG, hauteur 40-60px" />
    <ImageField label="Logo mobile" value={sec.logo_mobile_url} onChange={v => setField("logo_mobile_url", v)} onUpload={f => uploadFile(f, "logo_mobile_url")} uploading={uploading} hint="Optionnel — utilise le logo principal si vide" />
    <ImageField label="Logo footer" value={sec.logo_footer_url} onChange={v => setField("logo_footer_url", v)} onUpload={f => uploadFile(f, "logo_footer_url")} uploading={uploading} hint="Version claire/blanche pour fond sombre" />
    <ImageField label="Favicon" value={sec.favicon_url} onChange={v => setField("favicon_url", v)} onUpload={f => uploadFile(f, "favicon_url")} uploading={uploading} hint="PNG ou ICO carré, 32×32 ou 64×64" />
  </>
);

const ThemeEditor = ({ sec, setField }) => (
  <>
    <div className="grid md:grid-cols-3 gap-4">
      <Field label="Police des titres" hint="Choix Google Fonts">
        <select value={sec.font_heading || "Archivo"} onChange={e => setField("font_heading", e.target.value)} className="input" data-testid="theme-font-heading">
          {POPULAR_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>
      </Field>
      <Field label="Police du corps" hint="Texte courant">
        <select value={sec.font_body || "Outfit"} onChange={e => setField("font_body", e.target.value)} className="input" data-testid="theme-font-body">
          {POPULAR_FONTS.map(f => <option key={f} value={f} style={{ fontFamily: f }}>{f}</option>)}
        </select>
      </Field>
      <Field label="Police monospace (chiffres)">
        <select value={sec.font_mono || "JetBrains Mono"} onChange={e => setField("font_mono", e.target.value)} className="input">
          {POPULAR_FONTS.filter(f => /Mono|Code|Plex/.test(f)).map(f => <option key={f} value={f}>{f}</option>)}
        </select>
      </Field>
    </div>

    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
      <ColorField label="Couleur d'accent" value={sec.color_accent} onChange={v => setField("color_accent", v)} testId="theme-color-accent" />
      <ColorField label="Accent au survol" value={sec.color_accent_hover} onChange={v => setField("color_accent_hover", v)} />
      <ColorField label="Texte (encre)" value={sec.color_ink} onChange={v => setField("color_ink", v)} />
      <ColorField label="Fond de page" value={sec.color_bg} onChange={v => setField("color_bg", v)} />
    </div>

    <Field label="Taille des textes">
      <select value={sec.text_size_preset || "normal"} onChange={e => setField("text_size_preset", e.target.value)} className="input">
        <option value="compact">Compact</option>
        <option value="normal">Normal (recommandé)</option>
        <option value="large">Grand</option>
      </select>
    </Field>

    <label className="flex items-center gap-2 cursor-pointer">
      <input type="checkbox" checked={sec.dark_mode_enabled !== false} onChange={e => setField("dark_mode_enabled", e.target.checked)} className="w-4 h-4 accent-slate-900" data-testid="theme-dark-toggle"/>
      <span className="text-sm font-semibold">Activer le mode sombre (bouton dans le header)</span>
    </label>

    <div className="bg-[color:var(--bg-default)] border-2 border-dashed p-6 mt-4" style={{ borderColor: "var(--border-default)" }}>
      <div className="text-xs font-bold uppercase tracking-widest mb-3" style={{ color: "var(--text-secondary)" }}>Aperçu</div>
      <h2 className="font-display font-black text-3xl" style={{ fontFamily: `'${sec.font_heading || "Archivo"}', sans-serif`, color: sec.color_ink }}>Titre exemple</h2>
      <p className="mt-2" style={{ fontFamily: `'${sec.font_body || "Outfit"}', sans-serif`, color: sec.color_ink }}>Voici un exemple de paragraphe pour vérifier le rendu de votre typographie et de vos couleurs.</p>
      <button className="btn-brutal mt-4 px-5 py-2 text-xs font-bold uppercase tracking-wider" style={{ background: sec.color_accent, color: sec.color_ink, border: `1px solid ${sec.color_ink}` }}>Bouton exemple</button>
    </div>
  </>
);

const ColorField = ({ label, value, onChange, testId }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-widest mb-2 block" style={{ color: "var(--text-secondary)" }}>{label}</span>
    <div className="flex gap-2 items-center">
      <input type="color" value={value || "#000000"} onChange={e => onChange(e.target.value)} className="w-12 h-12 border border-slate-900 cursor-pointer bg-transparent" data-testid={testId}/>
      <input value={value || ""} onChange={e => onChange(e.target.value)} className="input flex-1 font-mono" />
    </div>
  </label>
);

const MenuEditor = ({ sec, setField }) => {
  const links = sec.header_links || [];
  const cols = sec.footer_columns || [];
  const socials = sec.socials || [];

  const updateLink = (i, k, v) => setField("header_links", links.map((l, idx) => idx === i ? { ...l, [k]: v } : l));
  const addLink = () => setField("header_links", [...links, { label: "Nouveau lien", url: "/", children: [] }]);
  const removeLink = (i) => setField("header_links", links.filter((_, idx) => idx !== i));
  const moveLink = (i, dir) => {
    const j = dir === "up" ? i - 1 : i + 1;
    if (j < 0 || j >= links.length) return;
    const next = [...links]; [next[i], next[j]] = [next[j], next[i]]; setField("header_links", next);
  };
  const addChild = (i) => updateLink(i, "children", [...(links[i].children || []), { label: "Sous-lien", url: "/" }]);
  const updateChild = (i, ci, k, v) => updateLink(i, "children", (links[i].children || []).map((c, idx) => idx === ci ? { ...c, [k]: v } : c));
  const removeChild = (i, ci) => updateLink(i, "children", (links[i].children || []).filter((_, idx) => idx !== ci));

  const updateCol = (i, k, v) => setField("footer_columns", cols.map((c, idx) => idx === i ? { ...c, [k]: v } : c));
  const addCol = () => setField("footer_columns", [...cols, { title: "Nouvelle colonne", links: [{ label: "Lien", url: "/" }] }]);
  const removeCol = (i) => setField("footer_columns", cols.filter((_, idx) => idx !== i));
  const updateColLink = (i, li, k, v) => updateCol(i, "links", (cols[i].links || []).map((l, idx) => idx === li ? { ...l, [k]: v } : l));
  const addColLink = (i) => updateCol(i, "links", [...(cols[i].links || []), { label: "Lien", url: "/" }]);
  const removeColLink = (i, li) => updateCol(i, "links", (cols[i].links || []).filter((_, idx) => idx !== li));

  const addSocial = () => setField("socials", [...socials, { type: "LinkedIn", url: "https://" }]);
  const updateSocial = (i, k, v) => setField("socials", socials.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeSocial = (i) => setField("socials", socials.filter((_, idx) => idx !== i));

  return (
    <>
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Menu principal</h3>
          <button type="button" onClick={addLink} data-testid="add-menu-link" className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white flex items-center gap-1"><Plus className="w-3 h-3"/>Lien</button>
        </div>
        <div className="space-y-3">
          {links.map((l, i) => (
            <div key={i} className="border p-4 bg-[color:var(--bg-default)]" style={{ borderColor: "var(--border-default)" }} data-testid={`menu-link-${i}`}>
              <div className="grid md:grid-cols-[1fr_1fr_auto] gap-2 items-end">
                <input value={l.label} onChange={e => updateLink(i, "label", e.target.value)} placeholder="Libellé" className="input"/>
                <input value={l.url} onChange={e => updateLink(i, "url", e.target.value)} placeholder="URL (/locaux ou https://...)" className="input font-mono text-xs"/>
                <div className="flex gap-1">
                  <button type="button" onClick={() => moveLink(i, "up")} className="border border-slate-900 w-8 h-9 hover:bg-slate-900 hover:text-white">↑</button>
                  <button type="button" onClick={() => moveLink(i, "down")} className="border border-slate-900 w-8 h-9 hover:bg-slate-900 hover:text-white">↓</button>
                  <button type="button" onClick={() => removeLink(i)} className="border border-slate-900 w-8 h-9 hover:bg-red-600 hover:text-white hover:border-red-600"><X className="w-3 h-3 mx-auto"/></button>
                </div>
              </div>
              {(l.children || []).length > 0 && (
                <div className="mt-3 pl-6 border-l-2 border-[color:var(--solar)] space-y-2">
                  {l.children.map((c, ci) => (
                    <div key={`child-${i}-${ci}`} className="grid md:grid-cols-[1fr_1fr_auto] gap-2">
                      <input value={c.label} onChange={e => updateChild(i, ci, "label", e.target.value)} placeholder="Sous-lien" className="input"/>
                      <input value={c.url} onChange={e => updateChild(i, ci, "url", e.target.value)} placeholder="URL" className="input font-mono text-xs"/>
                      <button type="button" onClick={() => removeChild(i, ci)} className="border border-slate-900 w-8 h-9 hover:bg-red-600 hover:text-white hover:border-red-600"><X className="w-3 h-3 mx-auto"/></button>
                    </div>
                  ))}
                </div>
              )}
              <button type="button" onClick={() => addChild(i)} className="mt-2 text-xs underline" style={{ color: "var(--text-secondary)" }}>+ Ajouter un sous-lien</button>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-6 border-t" style={{ borderColor: "var(--border-default)" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Colonnes du footer</h3>
          <button type="button" onClick={addCol} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white flex items-center gap-1"><Plus className="w-3 h-3"/>Colonne</button>
        </div>
        <div className="space-y-3">
          {cols.map((c, i) => (
            <div key={`col-${i}`} className="border p-4 bg-[color:var(--bg-default)]" style={{ borderColor: "var(--border-default)" }}>
              <div className="flex gap-2 items-center mb-3">
                <input value={c.title} onChange={e => updateCol(i, "title", e.target.value)} placeholder="Titre colonne" className="input flex-1"/>
                <button type="button" onClick={() => removeCol(i)} className="border border-slate-900 w-8 h-9 hover:bg-red-600 hover:text-white hover:border-red-600"><X className="w-3 h-3 mx-auto"/></button>
              </div>
              <div className="pl-4 border-l-2 border-[color:var(--solar)] space-y-2">
                {(c.links || []).map((l, li) => (
                  <div key={`coll-${i}-${li}`} className="grid md:grid-cols-[1fr_1fr_auto] gap-2">
                    <input value={l.label} onChange={e => updateColLink(i, li, "label", e.target.value)} placeholder="Libellé" className="input"/>
                    <input value={l.url} onChange={e => updateColLink(i, li, "url", e.target.value)} placeholder="URL" className="input font-mono text-xs"/>
                    <button type="button" onClick={() => removeColLink(i, li)} className="border border-slate-900 w-8 h-9 hover:bg-red-600 hover:text-white hover:border-red-600"><X className="w-3 h-3 mx-auto"/></button>
                  </div>
                ))}
                <button type="button" onClick={() => addColLink(i)} className="text-xs underline" style={{ color: "var(--text-secondary)" }}>+ Ajouter un lien</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="pt-6 border-t" style={{ borderColor: "var(--border-default)" }}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-display font-bold text-lg">Réseaux sociaux</h3>
          <button type="button" onClick={addSocial} className="text-xs font-bold uppercase border border-slate-900 px-3 py-1.5 hover:bg-slate-900 hover:text-white flex items-center gap-1"><Plus className="w-3 h-3"/>Réseau</button>
        </div>
        <div className="space-y-2">
          {socials.map((s, i) => (
            <div key={`social-${i}`} className="grid md:grid-cols-[1fr_2fr_auto] gap-2">
              <select value={s.type} onChange={e => updateSocial(i, "type", e.target.value)} className="input">
                {["LinkedIn","Facebook","Instagram","Twitter","YouTube","TikTok"].map(t => <option key={t}>{t}</option>)}
              </select>
              <input value={s.url} onChange={e => updateSocial(i, "url", e.target.value)} placeholder="https://..." className="input"/>
              <button type="button" onClick={() => removeSocial(i)} className="border border-slate-900 w-8 h-9 hover:bg-red-600 hover:text-white hover:border-red-600"><X className="w-3 h-3 mx-auto"/></button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const HeroEditor = ({ sec, setField, uploadFile, uploading }) => (
  <>
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Type de média"><select value={sec.media_type || "image"} onChange={e => setField("media_type", e.target.value)} className="input"><option value="image">Image fixe</option><option value="video">Vidéo (autoplay muet, en boucle)</option></select></Field>
      <Field label="URL du média"><input value={sec.media_url || ""} onChange={e => setField("media_url", e.target.value)} className="input" /></Field>
    </div>
    <label className="btn-brutal bg-[color:var(--bg-surface)] border border-slate-900 px-4 py-2 text-xs font-bold uppercase tracking-wider cursor-pointer inline-flex items-center gap-2">
      <Upload className="w-3 h-3"/>{uploading ? "Upload..." : `Uploader ${sec.media_type === "video" ? "une vidéo" : "une image"}`}
      <input type="file" accept={sec.media_type === "video" ? "video/*" : "image/*"} onChange={e => e.target.files[0] && uploadFile(e.target.files[0], "media_url")} className="hidden" />
    </label>
    <Field label="Kicker (texte du badge en haut)"><input value={sec.kicker || ""} onChange={e => setField("kicker", e.target.value)} className="input" /></Field>
    <div className="grid md:grid-cols-3 gap-4">
      <Field label="Titre ligne 1"><input value={sec.title_line1 || ""} onChange={e => setField("title_line1", e.target.value)} className="input" /></Field>
      <Field label="Titre ligne 2"><input value={sec.title_line2 || ""} onChange={e => setField("title_line2", e.target.value)} className="input" /></Field>
      <Field label="Titre accent"><input value={sec.title_accent || ""} onChange={e => setField("title_accent", e.target.value)} className="input" /></Field>
    </div>
    <Field label="Sous-titre"><textarea rows={3} value={sec.subtitle || ""} onChange={e => setField("subtitle", e.target.value)} className="input resize-none" /></Field>
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Bouton 1 - libellé"><input value={sec.cta_primary_label || ""} onChange={e => setField("cta_primary_label", e.target.value)} className="input" /></Field>
      <Field label="Bouton 1 - lien"><input value={sec.cta_primary_link || ""} onChange={e => setField("cta_primary_link", e.target.value)} className="input font-mono" /></Field>
      <Field label="Bouton 2 - libellé"><input value={sec.cta_secondary_label || ""} onChange={e => setField("cta_secondary_label", e.target.value)} className="input" /></Field>
      <Field label="Bouton 2 - lien"><input value={sec.cta_secondary_link || ""} onChange={e => setField("cta_secondary_link", e.target.value)} className="input font-mono" /></Field>
    </div>
  </>
);

const AboutEditor = ({ sec, setField, uploadFile, uploading }) => {
  const values = sec.values || [];
  const updateValue = (i, k, v) => setField("values", values.map((it, idx) => idx === i ? { ...it, [k]: v } : it));
  const addValue = () => setField("values", [...values, { title: "", desc: "" }]);
  const removeValue = (i) => setField("values", values.filter((_, idx) => idx !== i));
  return (
    <>
      <Field label="Kicker"><input value={sec.kicker || ""} onChange={e => setField("kicker", e.target.value)} className="input" /></Field>
      <Field label="Titre"><input value={sec.title || ""} onChange={e => setField("title", e.target.value)} className="input" /></Field>
      <Field label="Introduction"><textarea rows={3} value={sec.intro || ""} onChange={e => setField("intro", e.target.value)} className="input resize-none" /></Field>
      <Field label="Contenu détaillé"><textarea rows={6} value={sec.content || ""} onChange={e => setField("content", e.target.value)} className="input resize-none" /></Field>
      <ImageField label="Image" value={sec.image_url} onChange={v => setField("image_url", v)} onUpload={f => uploadFile(f, "image_url")} uploading={uploading} />
      <div>
        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-bold uppercase tracking-widest" style={{ color: "var(--text-secondary)" }}>Valeurs / Points clés</span>
          <button type="button" onClick={addValue} className="text-xs font-bold uppercase border border-slate-900 px-2 py-1 hover:bg-slate-900 hover:text-white flex items-center gap-1"><Plus className="w-3 h-3"/>Ajouter</button>
        </div>
        <div className="space-y-2">
          {values.map((v, i) => (
            <div key={`val-${i}`} className="grid md:grid-cols-[1fr_2fr_auto] gap-2 items-center border p-3" style={{ borderColor: "var(--border-default)" }}>
              <input value={v.title} onChange={e => updateValue(i, "title", e.target.value)} placeholder="Titre" className="input" />
              <input value={v.desc} onChange={e => updateValue(i, "desc", e.target.value)} placeholder="Description" className="input" />
              <button type="button" onClick={() => removeValue(i)} className="text-red-600"><X className="w-4 h-4"/></button>
            </div>
          ))}
        </div>
      </div>
    </>
  );
};

const ContactEditor = ({ sec, setField }) => (
  <>
    <div className="grid md:grid-cols-2 gap-4">
      <Field label="Téléphone"><input value={sec.phone || ""} onChange={e => setField("phone", e.target.value)} className="input font-mono" /></Field>
      <Field label="Email"><input value={sec.email || ""} onChange={e => setField("email", e.target.value)} className="input" /></Field>
      <Field label="Ville"><input value={sec.city || ""} onChange={e => setField("city", e.target.value)} className="input" /></Field>
      <Field label="Horaires"><input value={sec.hours || ""} onChange={e => setField("hours", e.target.value)} className="input" /></Field>
    </div>
    <Field label="Adresse complète"><input value={sec.address || ""} onChange={e => setField("address", e.target.value)} className="input" /></Field>
    <Field label="URL Google Maps embed" hint="Astuce : Google Maps → Partager → Intégrer une carte → copiez le `src` de l'iframe.">
      <input value={sec.maps_embed || ""} onChange={e => setField("maps_embed", e.target.value)} className="input font-mono text-xs" />
    </Field>
  </>
);
